/**
 * Feb 2026 R293zz · Catalog spec preview thumbnail.
 *
 * Lazy-fetches a single attachment as a blob (so the auth token on
 * axios travels along) and either renders an inline image, a PDF
 * iframe preview, or an icon + "Open" link for other mime types.
 * Blob URLs are revoked on unmount.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  FileText, Image as ImageIcon, FileSpreadsheet,
  File as FileIcon, Loader2, Eye,
} from "lucide-react";

function iconFor(ctype = "") {
  if (ctype.startsWith("image/")) return ImageIcon;
  if (ctype === "application/pdf") return FileText;
  if (ctype.includes("spreadsheet") || ctype.includes("excel") || ctype === "text/csv") return FileSpreadsheet;
  return FileIcon;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function CatalogSpecThumb({ itemId, att }) {
  const ctype = att.content_type || "";
  const isImage = ctype.startsWith("image/");
  const isPdf = ctype === "application/pdf";
  const Icon = iconFor(ctype);

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Images are fetched eagerly (small, previewed inline). PDFs and
  // everything else are fetched only when the user clicks "Open" —
  // keeps the catalog picker light.
  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/items/${itemId}/attachments/${att.id}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const u = URL.createObjectURL(r.data);
        setBlobUrl(u);
      } catch (e) {
        if (!cancelled) setErr("Couldn't load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isImage, itemId, att.id]);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  const openBlob = async () => {
    if (blobUrl) {
      window.open(blobUrl, "_blank", "noopener");
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/items/${itemId}/attachments/${att.id}`, {
        responseType: "blob",
      });
      const u = URL.createObjectURL(r.data);
      setBlobUrl(u);
      window.open(u, "_blank", "noopener");
    } catch (e) {
      setErr("Couldn't load");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center gap-2 bg-white border border-amber-200 rounded px-2 py-1.5"
      data-testid={`spec-thumb-${att.id}`}
    >
      {/* Thumbnail */}
      <div className="w-16 h-16 shrink-0 bg-muted/40 border border-border rounded flex items-center justify-center overflow-hidden">
        {isImage && blobUrl && (
          <img
            src={blobUrl}
            alt={att.filename}
            className="w-full h-full object-cover"
          />
        )}
        {isImage && !blobUrl && (loading
          ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          : err
            ? <span className="text-[9px] text-rose-500 text-center px-1">{err}</span>
            : <Icon className="h-5 w-5 text-muted-foreground" />)}
        {!isImage && <Icon className="h-5 w-5 text-muted-foreground" />}
      </div>
      {/* Meta + action */}
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium truncate" title={att.filename}>
          {att.filename}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {fmtSize(att.size)}
        </div>
        {(isPdf || !isImage) && (
          <button
            type="button"
            onClick={openBlob}
            disabled={loading}
            className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline mt-0.5"
            data-testid={`spec-thumb-open-${att.id}`}
          >
            {loading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Eye className="h-2.5 w-2.5" />}
            Open
          </button>
        )}
      </div>
    </div>
  );
}
